import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { T } from "../tokens";
import { LiveDot } from "../components/LiveDot";
import { useReveal } from "../hooks/useMotion";
import { useAuthStore } from "../store";
import { AdminUsers } from "./admin/Users";
import { AdminJobs } from "./admin/Jobs";
import { AdminCrossMatches } from "./admin/CrossMatches";
import { AdminInventoryControl } from "./admin/InventoryControl";

type AdminMetrics = {
  totalUsers: number;
  activeUsers: number;
  usersWithActiveResumes: number;
  activeJobs: number;
  totalMatches: number;
  jobsLast24h: number;
  matchesLast24h: number;
};

function MetricRow({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "10px 0", borderBottom: `1px solid ${T.b0}` }}>
      <span style={{ fontFamily: T.body, fontSize: "12px", color: T.t2 }}>{label}</span>
      <span style={{ fontFamily: T.mono, fontSize: "14px", fontWeight: 600,
        color: accent ?? T.t0, fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
}

type Section = "overview" | "users" | "jobs" | "matches" | "inventory";

export function Admin() {
  const token = useAuthStore((s) => s.token);
  const [section, setSection] = useState<Section>("overview");
  const headerRev = useReveal(0, 8);
  const card1Rev  = useReveal(80, 8);
  const card2Rev  = useReveal(160, 8);

  const q = useQuery({
    queryKey: ["admin-metrics", token ?? "anonymous"],
    queryFn: async () => {
      const { data } = await axios.get<AdminMetrics>("/api/admin/metrics");
      return data;
    },
    enabled: !!token,
    retry: false,
    refetchInterval: 60_000,
  });

  const status = (q.error as { response?: { status?: number } } | undefined)?.response?.status;

  return (
    <div style={{ maxWidth: section === "overview" ? "640px" : "100%" }}>

      {/* ── Header ───────────────────────────────────────────────── */}
      <div style={{ ...headerRev, marginBottom: "28px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3,
            letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Operations
          </span>
          {q.isSuccess && (
            <>
              <span style={{ color: T.t3, fontSize: "10px" }}>·</span>
              <LiveDot color={T.ok} size={5}/>
              <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.ok }}>live</span>
            </>
          )}
        </div>
        <h1 style={{ fontFamily: T.display, fontSize: "26px", fontWeight: 700, color: T.t0, margin: 0 }}>
          Admin
        </h1>
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, marginTop: "4px" }}>
          Read-only platform counters. Auto-refreshes every minute.
        </p>
      </div>

      {!token && (
        <p style={{ fontFamily: T.body, color: T.t2 }}>Login required.</p>
      )}
      {token && q.isLoading && (
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t3 }}>Loading metrics…</p>
      )}
      {status === 403 && (
        <p style={{ fontFamily: T.body, color: T.err, fontSize: "13px" }}>
          Forbidden — admin access required.
        </p>
      )}
      {q.isError && status !== 403 && token && (
        <p style={{ fontFamily: T.body, color: T.err, fontSize: "13px" }}>Failed to load metrics.</p>
      )}

      {/* ── Section tabs ─────────────────────────────────────────────── */}
      {q.data && (
        <div style={{ display: "flex", gap: 0, marginBottom: "20px",
          borderBottom: `1px solid ${T.b0}` }}>
          {(["overview", "users", "jobs", "matches", "inventory"] as Section[]).map((s) => (
            <button
              key={s} type="button"
              onClick={() => setSection(s)}
              style={{
                padding: "7px 16px", background: "none", border: "none",
                borderBottom: `2px solid ${section === s ? T.teal : "transparent"}`,
                color: section === s ? T.teal : T.t3,
                fontFamily: T.mono, fontSize: "11px", letterSpacing: "0.08em",
                textTransform: "capitalize", cursor: "pointer",
                transition: "color 0.15s",
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* ── Users section ─────────────────────────────────────────────── */}
      {q.data && section === "users" && <AdminUsers />}

      {/* ── Jobs section ─────────────────────────────────────────────── */}
      {q.data && section === "jobs" && <AdminJobs />}

      {/* ── Matches section ───────────────────────────────────────────── */}
      {q.data && section === "matches" && <AdminCrossMatches />}

      {/* ── Inventory Control Center ──────────────────────────────────── */}
      {q.data && section === "inventory" && <AdminInventoryControl />}

      {/* ── Overview section ──────────────────────────────────────────── */}
      {q.data && section === "overview" && (
        <>
          {/* Platform counters */}
          <div style={{ ...card1Rev, background: T.s1, border: `1px solid ${T.b0}`,
            borderRadius: "10px", padding: "16px 20px", marginBottom: "12px" }}>
            <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, letterSpacing: "0.10em",
              textTransform: "uppercase", marginBottom: "4px" }}>Platform</div>
            <MetricRow label="Total users"              value={q.data.totalUsers}/>
            <MetricRow label="Active users"             value={q.data.activeUsers} accent={T.ok}/>
            <MetricRow label="Users with active resume" value={q.data.usersWithActiveResumes}/>
          </div>

          {/* Job/match counters */}
          <div style={{ ...card2Rev, background: T.s1, border: `1px solid ${T.b0}`,
            borderRadius: "10px", padding: "16px 20px", marginBottom: "12px" }}>
            <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, letterSpacing: "0.10em",
              textTransform: "uppercase", marginBottom: "4px" }}>Discovery · 24h</div>
            <MetricRow label="Active jobs"      value={q.data.activeJobs}/>
            <MetricRow label="Jobs last 24h"    value={q.data.jobsLast24h}    accent={q.data.jobsLast24h > 0 ? T.tealSoft : T.t3}/>
            <MetricRow label="Total matches"    value={q.data.totalMatches}/>
            <MetricRow label="Matches last 24h" value={q.data.matchesLast24h} accent={q.data.matchesLast24h > 0 ? T.ok : T.t3}/>
          </div>

          {/* Extension download */}
          <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "10px", padding: "16px 20px" }}>
            <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, letterSpacing: "0.10em",
              textTransform: "uppercase", marginBottom: "10px" }}>Chrome Extension</div>
            <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t1, margin: "0 0 12px", lineHeight: 1.5 }}>
              Manual single-job capture only. Install: chrome://extensions → Developer mode → Load unpacked.
            </p>
            <button
              type="button"
              onClick={async () => {
                if (!token) return;
                const res = await axios.get("/api/admin/extension/download?env=production", {
                  responseType: "blob",
                  headers: { Authorization: `Bearer ${token}` },
                });
                const blob = new Blob([res.data], { type: String(res.headers["content-type"] || "application/zip") });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "calibre-extension-production.zip";
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              }}
              style={{ padding: "8px 14px", background: T.s2, border: `1px solid ${T.b1}`,
                borderRadius: "6px", color: T.t0, fontFamily: T.body, fontSize: "12px",
                fontWeight: 600, cursor: "pointer" }}
            >
              Download extension ZIP
            </button>
          </div>
        </>
      )}
    </div>
  );
}
